/**
 * Subject model + permission checks (03-07 §3).
 *
 * A `Subject` is the auth session's view of the current user: the permissions they hold. The
 * package never fetches — apps inject this from `@smartsash/auth` (03-07 §4).
 *
 * It carried roles and scopes until HS-1387. Neither was ever populated: the backend reports no
 * user-level role on `/Auth/me` (a role belongs to an assignment, not to an identity, since
 * ADR-0001), so both apps passed `[]` and the role branch of `hasPermission` could not run. The
 * role CATALOGUE below stays — `ROLES`, `ROLE_HIERARCHY`, `roleRank`, `isRoleName` all describe
 * the role of a user being *edited* in admin, which is a different subject entirely.
 */

import { PERMISSIONS, ROLES, type PermissionKey, type RoleName } from './matrix.config';

const PERMISSION_KEY_SET = new Set<string>(Object.keys(PERMISSIONS));
const ROLE_NAME_SET = new Set<string>(Object.keys(ROLES));

/**
 * Narrowing the session onto the catalogue (HS-866).
 *
 * The auth session carries permission keys and role names as plain strings, while every check
 * in this package takes the typed union. Both apps need the same bridge, so it lives here
 * rather than being reimplemented per app — and it must be a set membership test, not the `in`
 * operator, which would also admit `Object.prototype` members such as `toString`.
 *
 * Unknown values are dropped rather than cast. That is the safe direction: a key the catalogue
 * does not define grants nothing, exactly as an absent key would, so a backend serving a
 * different vocabulary degrades to "no permissions" instead of lying to the type system.
 */
export function isPermissionKey(value: string): value is PermissionKey {
  return PERMISSION_KEY_SET.has(value);
}

export function isRoleName(value: string): value is RoleName {
  return ROLE_NAME_SET.has(value);
}

/** The session keys the catalogue recognises, in their original order. */
export function toPermissionKeys(raw: readonly string[]): PermissionKey[] {
  return raw.filter(isPermissionKey);
}

/** Keys the session carries that the catalogue does not know — drift, surfaced for diagnostics. */
export function unknownPermissionKeys(raw: readonly string[]): string[] {
  return raw.filter((key) => !isPermissionKey(key));
}

export interface Subject {
  /** The session's effective permission keys — the whole authorization input. */
  permissions?: readonly PermissionKey[];
}

/**
 * Whether the subject may perform `key`. `FullAccess` is the wildcard and short-circuits to
 * `true`; otherwise the key must be among the session's effective permissions.
 *
 * UI show/hide only — the server remains authoritative (03-07 §1).
 */
export function hasPermission(subject: Subject, key: PermissionKey): boolean {
  const permissions = subject.permissions;
  if (!permissions) return false;
  return permissions.includes('FullAccess') || permissions.includes(key);
}
